import {
  CheckboxField,
  InputField,
  EfsMelcoEfsModelsFormat,
  EfsMelcoEfsModelsSchemaListItem,
  Search,
} from "melco-shared-logic";
import {
  Alert,
  Col,
  DesignTokens,
  Radio,
  Space,
  Table,
  TableActionBarRow,
  Title,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useDataAssignments } from "../../hooks/inputFormats/useDataAssignments";
import { useSchemaListIndex } from "../../hooks/inputFormats/useSchemaListIndex";
import { useUniqueField } from "../../hooks/inputFormats/useUniqueField";
import { useTableDataFilter } from "../../hooks/table/useTableDataFilter";
import { ToggleDataAssignmentVisibilityCheckbox } from "./ToggleDataAssignmentVisibilityCheckbox";

const { spacings } = DesignTokens;

const TableWrapper = styled.div`
  margin-left: -${spacings.xlarge}px;
  margin-right: -${spacings.xlarge}px;
`;

type DataAssignmentsProps = {
  inputFormat: EfsMelcoEfsModelsFormat;
};

export const DataAssignments: React.FC<DataAssignmentsProps> = ({
  inputFormat,
}) => {
  const { t } = useTranslation();

  const { key } = inputFormat;

  const dataAssignments = useDataAssignments();
  const [uniqueField, setUniqueField] = useUniqueField();

  const getSchemaListIndexForId = useSchemaListIndex();

  const [searchQuery, setSearchQuery, filteredDataAssignments] =
    useTableDataFilter(dataAssignments, ["display_name"]);

  const columns = [
    {
      title: t(
        "inputFormats.actions.dataAssignments.list.columns.display_name"
      ),
      key: "display_name",
      dataIndex: "display_name",
      render: (_: string, item: EfsMelcoEfsModelsSchemaListItem) => {
        const index = getSchemaListIndexForId(item.id ?? undefined);

        if (index == null) {
          return null;
        }

        return <InputField name={`schema_list[${index}].display_name`} />;
      },
      width: 300,
    },
    {
      title: t(
        `inputFormats.actions.dataAssignments.list.columns.${
          key === "XML" ? "xpath" : "column"
        }`
      ),
      key: "value",
      dataIndex: "value",
      render: (_: string, item: EfsMelcoEfsModelsSchemaListItem) => {
        return (
          <ValueOrFallback
            value={item.value}
            fallback={t("global.fallback.empty")}
          />
        );
      },
    },
    {
      title: t(
        "inputFormats.actions.dataAssignments.list.columns.unique_field"
      ),
      key: "unique_field",
      dataIndex: "unique_field",
      render: (_: string, item: EfsMelcoEfsModelsSchemaListItem) => {
        return (
          <Radio
            checked={uniqueField === item.value}
            onChange={() => setUniqueField(item.value ?? null)}
          />
        );
      },
    },
    {
      title: (
        <Space size="small">
          <ToggleDataAssignmentVisibilityCheckbox />
          {t("inputFormats.actions.dataAssignments.list.columns.show")}
        </Space>
      ),
      key: "show",
      dataIndex: "show",
      sorter: false,
      render: (_: string, item: EfsMelcoEfsModelsSchemaListItem) => {
        const index = getSchemaListIndexForId(item.id ?? undefined);

        if (index == null) {
          return null;
        }

        return <CheckboxField name={`schema_list[${index}].show`} />;
      },
      width: 150,
    },
  ];

  return (
    <>
      <Title level={5}>
        {t("inputFormats.detail.form.dataAssignments.title")}
      </Title>

      <Alert
        type="info"
        message={t("inputFormats.actions.dataAssignments.help")}
        withBackground
      />

      <TableWrapper>
        <TableActionBarRow>
          <Col>
            <Search
              onSearchInputChange={() => {}}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Col>
        </TableActionBarRow>
        <Table
          showSorterTooltip={false}
          sticky
          dataSource={filteredDataAssignments}
          columns={columns}
          rowKey="id"
          data-cy="data-assignments-table"
        />
      </TableWrapper>
    </>
  );
};
